// ─── Live → Communications ───────────────────────────────────────────────────
// Mirrors the XD reference: icon-tabs at the top, then an interface header card
// (Cellular / Wi-Fi / Iridium), then Modem Info and Signal Quality cards.
//
// Values come from the device's "comm info" MQTT message, decoded by the bridge
// into app_data.CommInfo and served by /api/project/<pid>/device/<did>/comm-info/.
// The device sends Type/IMEI/ICCID once at power-up and RSSI/RSRQ periodically,
// so we POLL and the numbers refresh on their own. Per spec: IMEI and ICCID are
// masked ("*****") for collaborators (the backend does the masking); RSSI/RSRQ
// are visible to everyone; RSRQ is not reported for Wi-Fi / Iridium.

import { useEffect, useState } from 'react'
import { Loader, AlertCircle } from 'lucide-react'
import { useDeviceStore } from '../../store/device-store'
import { fetchCommInfo } from '../../lib/projectsApi'
import LiveIconTabs from './LiveIconTabs'

const COMM_POLL_MS = 10000   // RSSI/RSRQ refresh cadence

const Card = ({ children, className = '' }) => (
  <div className={`bg-white border-gray-200 rounded-lg shadow-sm px-3 py-4 ${className}`}>
    {children}
  </div>
)

// Read-only field: label on the left, a bordered box on the right (XD design).
const ReadField = ({ label, value }) => (
  <div className="flex items-center gap-3 min-w-0">
    <span className="common-size15 text-xs text-[#4D4D4D] whitespace-nowrap">{label}</span>
    <input
      type="text"
      value={value ?? ''}
      readOnly
      className="flex-1 min-w-0 px-3 py-1.5 border border-[#D6D6D6] rounded-md text-xs text-gray-700 bg-white"
    />
  </div>
)

// Human label + whether RSRQ applies, from the reported interface type.
// (type codes: 0=Wifi 1=Cellular 2=Iridium 3=Radio → normalized by the backend.)
const INTERFACE = {
  cellular: { label: 'Cellular', rsrq: true },
  wifi:     { label: 'Wi-Fi',    rsrq: false },
  'wi-fi':  { label: 'Wi-Fi',    rsrq: false },
  iridium:  { label: 'Iridium',  rsrq: false },
  radio:    { label: 'Radio',    rsrq: false },
}

const fmtNum = (v) => (v == null || v === '' ? '' : String(v))
// Device sends e.g. "fair" → display "Fair" (Good / Fair / Poor / Unknown).
const fmtQuality = (q) => (!q ? '' : String(q).charAt(0).toUpperCase() + String(q).slice(1))

export default function CommunicationsTab({ projectId, deviceId, currentLiveIndex, setCurrentLiveIndex, devices = [], onSelectDevice, showHeader = true }) {
  const { isLoading, error, loadDevice } = useDeviceStore()
  const [comm, setComm] = useState(null)

  useEffect(() => {
    if (projectId && deviceId) loadDevice(projectId, deviceId)
  }, [projectId, deviceId, loadDevice])

  // Poll comm info while the tab is open for a device. Degrades gracefully: if
  // the device hasn't reported yet the fields stay blank.
  useEffect(() => {
    if (!projectId || !deviceId) { setComm(null); return }
    let cancelled = false
    const poll = async () => {
      try {
        const res = await fetchCommInfo(projectId, deviceId)
        if (!cancelled && res && res.success) setComm(res)
      } catch { /* transient — next tick retries */ }
    }
    poll()
    const id = setInterval(poll, COMM_POLL_MS)
    return () => { cancelled = true; clearInterval(id) }
  }, [projectId, deviceId])

  const header = showHeader ? (
    <Card className="!py-2 !border-none">
      <LiveIconTabs
        currentLiveIndex={currentLiveIndex}
        setCurrentLiveIndex={setCurrentLiveIndex}
        devices={devices}
        deviceId={deviceId}
        onSelectDevice={onSelectDevice}
      />
    </Card>
  ) : null

  if (!deviceId) {
    return (
      <div className="w-full h-full flex flex-col bg-[#F1F2F4] overflow-y-auto gap-5">
        {header}
        <div className="p-6 text-sm text-gray-500">Select a device to view its communications settings.</div>
      </div>
    )
  }
  if (isLoading) {
    return (
      <div className="w-full h-full flex flex-col bg-[#F1F2F4] overflow-y-auto gap-5">
        {header}
        <div className="p-6 flex items-center gap-2 text-sm text-gray-500"><Loader className="w-4 h-4 animate-spin" /> Loading device…</div>
      </div>
    )
  }

  const type = (comm?.type || '').toLowerCase()
  const iface = INTERFACE[type] || { label: comm?.type ? comm.type : 'Cellular', rsrq: true }
  const showRsrq = iface.rsrq
  // RSSI is a 1-5 scale for Iridium, dBm for every other interface.
  const rssiUnit = type === 'iridium' ? '1–5' : 'dBm'

  return (
    <div className="w-full h-full flex flex-col bg-[#F1F2F4] overflow-y-auto gap-5">
      {header}

      {error && (
        <div className="px-3 py-2 bg-red-50 border border-red-200 rounded text-sm text-red-700 flex items-center gap-2">
          <AlertCircle className="w-4 h-4" /> {error}
        </div>
      )}

      {/* Interface header card — reflects the reported Type. */}
      <Card>
        <h3 className="common-size18 text-sm font-bold text-[#4D4D4D]">{iface.label}</h3>
      </Card>

      {/* Modem Info — IMEI/ICCID (admin only; masked to ***** for collaborators). */}
      <Card>
        <h3 className="common-size15 text-sm border-b font-bold text-[#4D4D4D] mb-3 pb-3">Modem Info</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ReadField label="IMEI" value={comm?.imei ?? ''} />
          <ReadField label="ICCID (SIM ID)" value={comm?.iccid ?? ''} />
        </div>
      </Card>

      {/* Signal Quality — RSSI, RSRQ, Quality on one line (in that order). RSSI is
          1-5 for Iridium, dB otherwise; RSRQ only for Cellular; Quality is the
          device's descriptive string. */}
      <Card>
        <h3 className="common-size15 text-sm border-b font-bold text-[#4D4D4D] mb-3 pb-3">Signal Quality</h3>
        <div className={`grid grid-cols-1 gap-4 ${showRsrq ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
          <ReadField label={`RSSI (${rssiUnit})`} value={fmtNum(comm?.rssi)} />
          {showRsrq && <ReadField label="RSRQ (dB)" value={fmtNum(comm?.rsrq)} />}
          <ReadField label="Quality" value={fmtQuality(comm?.quality)} />
        </div>
      </Card>
    </div>
  )
}
